/**
 * TAMIX SAAS PLATFORM - Tenant API Service  
 * =========================================
 * Generated: 2025-09-22 - Session 6 Sprint 0
 * API calls for tenant management (CU-001)
 */

import type {
  Tenant,
  TenantDetail,
  CreateTenantData,
  UpdateTenantData,
  TenantListQuery,
  TenantListResponse,
  TenantStats,
  TenantConfigOptions
} from '@/types/tenant'

import { getApiV1Url } from '@/config/api'

class TenantApiService {
  private get baseURL(): string {
    return getApiV1Url()
  }

  // Get authentication headers with token
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('tamix-auth')
      ? JSON.parse(localStorage.getItem('tamix-auth')!)?.state?.token
      : null

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  // Handle API responses with error handling
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: `HTTP ${response.status}: ${response.statusText}`
      }))
      
      throw new Error(errorData.detail || 'API request failed')
    }

    return response.json()
  }

  // Get all tenants with optional filtering/pagination
  async getTenants(query: TenantListQuery = {}): Promise<TenantListResponse> {
    const params = new URLSearchParams()
    
    if (query.page) params.append('page', query.page.toString())
    if (query.limit) params.append('limit', query.limit.toString())
    if (query.status) params.append('status', query.status)
    if (query.search) params.append('search', query.search)
    if (query.sort_by) params.append('sort_by', query.sort_by)
    if (query.sort_order) params.append('sort_order', query.sort_order)

    const url = `${this.baseURL}/tenants?${params.toString()}`
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })

    return this.handleResponse<TenantListResponse>(response)
  }

  // Get single tenant by ID
  async getTenant(tenantId: string): Promise<TenantDetail> {
    const response = await fetch(`${this.baseURL}/tenants/${tenantId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })

    return this.handleResponse<TenantDetail>(response)
  }

  // Create new tenant
  async createTenant(tenantData: CreateTenantData): Promise<Tenant> {
    const response = await fetch(`${this.baseURL}/tenants`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(tenantData),
    })

    return this.handleResponse<Tenant>(response)
  }

  // Update existing tenant
  async updateTenant(tenantId: string, tenantData: UpdateTenantData): Promise<Tenant> {
    const response = await fetch(`${this.baseURL}/tenants/${tenantId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(tenantData),
    })

    return this.handleResponse<Tenant>(response)
  }

  // Delete tenant (soft delete)
  async deleteTenant(tenantId: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/tenants/${tenantId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: `HTTP ${response.status}: ${response.statusText}`
      }))
      
      throw new Error(errorData.detail || 'Delete failed')
    }
  }

  // Activate tenant
  async activateTenant(tenantId: string): Promise<Tenant> {
    const response = await fetch(`${this.baseURL}/tenants/${tenantId}/activate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    })

    return this.handleResponse<Tenant>(response)
  }

  // Suspend tenant
  async suspendTenant(tenantId: string): Promise<Tenant> {
    const response = await fetch(`${this.baseURL}/tenants/${tenantId}/suspend`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    })

    return this.handleResponse<Tenant>(response)
  }

  // Get tenant statistics (for platform admin dashboard)
  async getTenantStats(): Promise<TenantStats> {
    const response = await fetch(`${this.baseURL}/tenants/stats`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })

    return this.handleResponse<TenantStats>(response)
  }

  // Get tenant configuration options
  async getTenantConfigOptions(): Promise<TenantConfigOptions> {
    const response = await fetch(`${this.baseURL}/tenants/config-options`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })

    return this.handleResponse<TenantConfigOptions>(response)
  }

  // Validate tenant domain availability
  async validateDomain(domain: string): Promise<{ available: boolean; message?: string }> {
    const response = await fetch(`${this.baseURL}/tenants/validate-domain`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ domain }),
    })

    return this.handleResponse<{ available: boolean; message?: string }>(response)
  }

  // Export tenant data (for compliance)
  async exportTenantData(tenantId: string): Promise<Blob> {
    const response = await fetch(`${this.baseURL}/tenants/${tenantId}/export`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeaders(),
        'Accept': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error('Export failed')
    }

    return response.blob()
  }
}

// Export singleton instance
export const tenantApi = new TenantApiService()

// Export class for testing
export { TenantApiService }